import React, { useEffect, useState } from 'react';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import FileRepository from '@ckeditor/ckeditor5-upload/src/filerepository';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import AutoLink from '@ckeditor/ckeditor5-link/src/autolink';
import Base64UploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/base64uploadadapter';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import Command from '@ckeditor/ckeditor5-core/src/command';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import UploadAdapter from './uploadAdapter';
import { FileUploader } from '../FileUploader';
import { get } from '../../Services/Api.service';


import plainTextToHtml from '@ckeditor/ckeditor5-clipboard/src/utils/plaintexttohtml';
import { translate } from '../../../Services/translation';

export class CustomPastePlainText extends Plugin {
    static get pluginName() {
        return 'CustomPastePlainText'
    }

    static get requires() {
        return [PastePlainTextUI, PastePlainTextCommand]
    }

    init() {
        const editor = this.editor;

        editor.commands.add('customPastePlainText', new PastePlainTextCommand(editor));

        // The logic responsible for converting HTML to plain text.
        const clipboardPlugin = editor.plugins.get('ClipboardPipeline');
        const command = editor.commands.get('customPastePlainText');
        const editingView = editor.editing.view;

        editingView.document.on('clipboardInput', (evt, data) => {
            if (editor.isReadOnly || !command.value) {
                return;
            }

            const dataTransfer = data.dataTransfer;
            let content = plainTextToHtml(dataTransfer.getData('text/plain'));
            data.content = this.editor.data.htmlProcessor.toView(content);
        });
    }
};

class PastePlainTextUI extends Plugin {
    init() {
        const editor = this.editor;

        editor.ui.componentFactory.add('customPastePlainText', locale => {
            const view = new ButtonView(locale);
            const command = editor.commands.get('customPastePlainText');

            view.set({
                label: 'Paste as plain text',
                withText: true,
                tooltip: true,
                isToggleable: true
            });

            // A callback executed once the button is clicked.
            view.on('execute', () => {
                editor.execute('customPastePlainText');
            });

            view.bind('isOn', 'isEnabled').to(command, 'value', 'isEnabled');

            return view;
        });
    }
};

class PastePlainTextCommand extends Command {
    refresh() {
        // Disable the command if the editor is in read-only mode.
        this.isEnabled = !this.editor.isReadOnly;
    }

    execute() {
        // Activate pasting plain text.
        this.value = !this.value;
    }
}

const field = ({ headerName, label, typeName, onChangeHandler, updateData, fieldValue, isDisabled, selectOptions, onChangeHandlerBoolean }) => {
    const value = fieldValue || "";
    const [options, setOptions] = useState([]);
    useEffect(() => {
        if (selectOptions && selectOptions.endpoint) {
            const { endpoint, name, id } = selectOptions;
            get(endpoint).then(resp => resp.json()).then(resp => {
                if (resp.length) {
                    onChangeHandler({ target: { name: headerName, value: resp[0][id] } });
                    setOptions(resp.map(option => <option key={option[id]} value={option[id]}>{option[name]}</option>));
                }
            });
        } else {
            setOptions([]);
        }
    }, [selectOptions]);
    const [currentImage, setCurrentImage] = useState(null);
    switch (typeName) {
        case "Editor":
            return <>{label}:
                <CKEditor
                    editor={ClassicEditor}
                    data={value}
                    className="editor"
                    disabled={isDisabled}
                    onReady={(editor) => {
                        // You can store the "editor" and use when it is needed.
                        editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new UploadAdapter(loader);                        
                    }} 
                    config={{
                        toolbar: [
                            "selectAll",
                            "customPastePlainText",
                            "undo",
                            "redo",
                            "bold",
                            "italic",
                            "blockQuote",
                            "ckfinder",
                            "imageTextAlternative",
                            "imageUpload",
                            "heading",
                            "indent",
                            "outdent",
                            "link",
                            "numberedList",
                            "bulletedList",
                            "mediaEmbed",
                            "insertTable",
                            "tableColumn",
                            "tableRow",
                            "mergeTableCells"
                        ],
                        image: {
                            // Configure the available styles.
                            styles: ["alignLeft", "alignCenter", "alignRight"],
                            sizes: ["50%", "75%", "100%"],
                            // You need to configure the image toolbar, too, so it shows the new style
                            // buttons as well as the resize buttons.
                            toolbar: [
                                "imageStyle:alignLeft",
                                "imageStyle:alignCenter",
                                "imageStyle:alignRight",
                                "|",
                                "imageTextAlternative"
                            ]
                        },
                        plugins: [
                            FileRepository,
                            CustomPastePlainText,
                            Alignment,
                            Autoformat,
                            AutoLink,
                            Base64UploadAdapter,
                            BlockQuote,
                            Bold,
                            CloudServices,
                            Essentials,
                            Heading,
                            Image,
                            ImageCaption,
                            ImageStyle,
                            ImageToolbar,
                            ImageUpload,
                            Indent,
                            Italic,
                            Link,
                            List,
                            MediaEmbed,
                            Paragraph,
                            PasteFromOffice,
                            Table,
                            TableToolbar,
                            TextTransformation,
                        ]
                    }}                 
                    onChange={(event, editor) => updateData(headerName, editor.getData())}
                />
            </>
        case "Date":
            return <>{label}: <input disabled={isDisabled} type="date" name={headerName} onChange={onChangeHandler} value={value.split('T')[0]} /></>
        case "Number":
            return <>{label}: <input disabled={isDisabled} type="number" name={headerName} onChange={onChangeHandler} value={value} /></>
        case "Email":
            return <>{label}: <input disabled={isDisabled} type="text" name={headerName} onChange={onChangeHandler} value={value} /></>
        case "Url":
            return <>{label}: <input disabled={isDisabled} type="text" name={headerName} onChange={onChangeHandler} value={value} /></>
        case "String":
            return <>{label}: <input disabled={isDisabled} type="text" name={headerName} onChange={onChangeHandler} value={value} /></>
        case "Boolean":
            return <>{label}: <input className='checkbox-wrapper' disabled={isDisabled} type="checkbox" name={headerName} onChange={onChangeHandlerBoolean} defaultValue={value} /></>
        case "Select":
            return <>{label}:
                <select onChange={onChangeHandler} name={headerName} value={value}>
                    {options ? options : null}
                </select>
            </>
        case "Image":
            return <>
                <label>{label}: <FileUploader onFileSelectSuccess={(file) => {
                    let fr = new FileReader();
                    fr.onload = () => {
                        updateData(headerName, file);
                        setCurrentImage(fr.result);
                    };
                    fr.readAsDataURL(file);
                }} /></label>
                <div className="columns small-12">
                    {currentImage ? <img src={currentImage} /> : (value ? <img src={`${window.location.origin}${value}`} /> : null)}
                </div></>
        default:
            return <>{label}: <input disabled={isDisabled} type="text" name={headerName} onChange={onChangeHandler} value={value} /></>
    }
}

export const CRUDFormField = ({ headerName, label, typeName, onChangeHandler, onChangeHandlerBoolean, updateData, fieldValue, isDisabled, errors, selectOptions }) => {
    return <div className={`columns small-12 ${errors[headerName] ? "backend__validation-error" : ""}`}>
        {field({ headerName, label, typeName, onChangeHandler, updateData, fieldValue, isDisabled, selectOptions })}
        <span className='error'> {errors[headerName] && errors[headerName].type === 'required' && `${label} is required`}</span>
        <span className='error'> {errors[headerName] && errors[headerName].type === 'min' && `${label} is too short`}</span>
        <span className='error'> {errors[headerName] && errors[headerName].type === 'max' && `${label} is too long`}</span>
        <span className='error'> {errors[headerName] && errors[headerName].type && translate("coptikk.adminportal.validation.invalidformat")}</span>
    </div>;
}
